// Libs
import { Select } from '@instructure/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
// Others
import { IBaseSelectOption } from '~/utils/interface/common';
// Styles, images, icons

type Props = {
  value?: string;
  label?: string;
  options: IBaseSelectOption[];
  onChange?: (option: IBaseSelectOption) => void;
};

const BaseSelect = (props: Props) => {
  //#region Destructuring Props
  const { value, label, options, onChange } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [inputValue, setInputValue] = useState<string>();
  const [isShowingOptions, setIsShowingOptions] = useState<boolean>(false);
  const [highlightedOptionId, setHighlightedOptionId] = useState<string>();
  const [selectedOptionId, setSelectedOptionId] = useState<string>();
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const option = options.find((option) => option.value === value);
    if (option) {
      setInputValue(option?.label);
      setSelectedOptionId(option.value);
      return;
    }

    setInputValue(undefined);
    setSelectedOptionId(undefined);
  }, [options, value]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleBlur = () => {
    setHighlightedOptionId(undefined);
  };

  const handleShowOptions = (event: React.SyntheticEvent) => {
    setIsShowingOptions(true);
  };

  const handleHideOptions = (event: React.SyntheticEvent) => {
    const option = options.find((option) => option.value === selectedOptionId);
    setIsShowingOptions(false);
    setHighlightedOptionId(undefined);
    setInputValue(selectedOptionId ? option?.label : undefined);
  };

  const handleHighlightOption = (event: React.SyntheticEvent, data: { id?: string }) => {
    event.persist();
    const option = options.find((option) => option.value === data.id);
    setHighlightedOptionId(data.id);
    setInputValue((prevState) => (event.type === 'keydown' ? option?.label : prevState));
  };

  const handleSelectOption = (event: React.SyntheticEvent, data: { id?: string }) => {
    const option = options.find((option) => option.value === data.id);
    if (!option) return;

    setSelectedOptionId(data.id);
    setInputValue(option?.label);
    setIsShowingOptions(false);
    onChange && onChange(option);
  };
  //#endregion Handle Function

  return (
    <Select
      renderLabel={label}
      inputValue={inputValue ?? t('common_base_select_label')}
      isShowingOptions={isShowingOptions}
      onBlur={handleBlur}
      onRequestShowOptions={handleShowOptions}
      onRequestHideOptions={handleHideOptions}
      onRequestHighlightOption={handleHighlightOption}
      onRequestSelectOption={handleSelectOption}
    >
      {options.map((option) => {
        return (
          <Select.Option
            id={option.value}
            key={option.value}
            isHighlighted={option.value === highlightedOptionId}
            isSelected={option.value === selectedOptionId}
            renderBeforeLabel={option.renderBeforeLabel}
          >
            {option.label}
          </Select.Option>
        );
      })}
    </Select>
  );
};

export default BaseSelect;
